import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useConsentPreferences from '../hooks/useConsentPreferences';
import {
  CONSENT_OPEN_EVENT,
  setConsentPreferences,
  wasConsentStorageWriteSuccessful
} from '../utils/consent';
import { isEmbedConsentContext, isPurchaseConsentRoute, suppressUnsolicitedConsentBanner } from '../utils/consentUx';
import { startEmbedConsentBridge } from '../utils/embedConsentBridge';
import {
  clearConsentJourneyDismissal,
  dismissConsentJourney,
  getConsentVariant,
  hasConsentMeasurementObjection,
  isConsentJourneyDismissed,
  objectToConsentMeasurement,
  recordConsentFunnelAction,
  resumeConsentMeasurement
} from '../utils/consentExperiment';
import './TrackingConsent.css';
import TrackingNotice from './TrackingNotice';

function TrackingConsent() {
  const location = useLocation();
  const consent = useConsentPreferences();
  const [showPreferences, setShowPreferences] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  const [sessionSuppressed, setSessionSuppressed] = useState(isConsentJourneyDismissed);
  const [variant, setVariant] = useState(null);
  const [journeyStarted, setJourneyStarted] = useState(false);
  const [ticketSelectorOpen, setTicketSelectorOpen] = useState(false);
  const [measurementObjected, setMeasurementObjected] = useState(hasConsentMeasurementObjection);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);
  const bannerWasVisibleRef = useRef(false);

  const openPreferences = useCallback((event) => {
    if (window.LVMTrackingNotice?.enabled()) { window.location.assign(window.LVMTrackingNotice.privacyUrl('/privacy')); return; }
    const invoker = event?.currentTarget;
    previousFocusRef.current = invoker instanceof HTMLElement ? invoker : document.activeElement;
    setShowPreferences(true);
  }, []);

  useEffect(() => startEmbedConsentBridge({ location }), [location.pathname, location.search]);

  useEffect(() => {
    setAnalytics(consent.analytics);
    setMarketing(consent.marketing);
  }, [consent.analytics, consent.marketing]);

  useEffect(() => {
    window.addEventListener(CONSENT_OPEN_EVENT, openPreferences);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, openPreferences);
  }, [openPreferences]);

  useEffect(() => {
    const show = () => setContentReady(true);
    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(show, { timeout: 1200 });
      return () => window.cancelIdleCallback?.(idleId);
    }
    const timerId = window.setTimeout(show, 0);
    return () => window.clearTimeout(timerId);
  }, []);

  const isPending = consent.status === 'pending';
  const isEmbed = isEmbedConsentContext(location);
  const verifiedEmbedReturn = consent.verifiedEmbedReturn === true;
  const isPurchaseRoute = isPurchaseConsentRoute(location, { verifiedEmbedReturn });
  const suppressBanner = suppressUnsolicitedConsentBanner(location, { verifiedEmbedReturn });

  useEffect(() => {
    if (!isPurchaseRoute || typeof document === 'undefined' || !document.body) {
      setTicketSelectorOpen(false);
      return undefined;
    }
    const sync = () => setTicketSelectorOpen(Boolean(
      document.querySelector('.ticket-sidebar.mobile-expanded')
    ));
    sync();
    if (typeof MutationObserver !== 'function') return undefined;
    const observer = new MutationObserver(sync);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true
    });
    return () => observer.disconnect();
  }, [isPurchaseRoute]);

  const bannerVisible = (
    contentReady
    && !sessionSuppressed
    && isPending
    && !showPreferences
    && !suppressBanner
    && !ticketSelectorOpen
  );
  const bannerDisplayVisible = bannerVisible && Boolean(variant);

  const save = (categories, source) => {
    const wasPending = isPending;
    const withdrewOptionalPurpose = !wasPending && (
      (consent.analytics === true && categories.analytics !== true)
      || (consent.marketing === true && categories.marketing !== true)
    );
    const effectiveSource = withdrewOptionalPurpose ? 'withdraw' : source;
    const selection = categories.analytics && categories.marketing
      ? 'all_optional'
      : (categories.analytics
        ? 'analytics_only'
        : (categories.marketing ? 'marketing_only' : 'none_optional'));
    setConsentPreferences(categories, effectiveSource);
    if (effectiveSource === 'withdraw') {
      recordConsentFunnelAction('withdraw', {
        selection,
        existingAssignmentOnly: true
      });
    } else if (wasPending) {
      const action = effectiveSource === 'accept_all'
        ? 'accept_all'
        : (effectiveSource === 'reject_nonessential' ? 'reject_nonessential' : 'customise_save');
      recordConsentFunnelAction(action, { selection });
    }
    if (!wasConsentStorageWriteSuccessful()) recordConsentFunnelAction('storage_failure');
    clearConsentJourneyDismissal();
    setShowPreferences(false);
  };

  useEffect(() => {
    if (bannerVisible && !journeyStarted) {
      setVariant(getConsentVariant());
      recordConsentFunnelAction('eligible_visit');
      setJourneyStarted(true);
    }
  }, [bannerVisible, journeyStarted]);

  useEffect(() => {
    if (bannerDisplayVisible && !bannerWasVisibleRef.current) {
      recordConsentFunnelAction('banner_impression');
    }
    bannerWasVisibleRef.current = bannerDisplayVisible;
  }, [bannerDisplayVisible]);

  useEffect(() => {
    if (isPending && suppressBanner) setSessionSuppressed(true);
  }, [isPending, suppressBanner]);

  useEffect(() => {
    if (suppressBanner) setShowPreferences(false);
  }, [suppressBanner]);

  useEffect(() => {
    if (!showPreferences || typeof document === 'undefined' || !document.body) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showPreferences]);

  useEffect(() => {
    if (!showPreferences) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setShowPreferences(false);
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter((element) => element.getAttribute('aria-hidden') !== 'true');
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.setTimeout(() => dialogRef.current?.focus?.(), 0);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      const previousFocus = previousFocusRef.current;
      previousFocusRef.current = null;
      window.setTimeout(() => {
        const fallback = document.querySelector('.tracking-consent__actions button:last-child');
        const target = previousFocus?.isConnected ? previousFocus : fallback;
        target?.focus?.();
      }, 0);
    };
  }, [showPreferences]);

  const dismiss = () => {
    dismissConsentJourney();
    setSessionSuppressed(true);
  };

  const toggleMeasurement = () => {
    if (measurementObjected) {
      resumeConsentMeasurement();
      setMeasurementObjected(false);
    } else {
      objectToConsentMeasurement();
      setMeasurementObjected(true);
    }
  };

  if (isEmbed) return null;
  if (window.LVMTrackingNotice?.enabled()) return <TrackingNotice consent={consent} />;

  return (
    <>
      {bannerDisplayVisible && (
        <div className="tracking-consent__backdrop tracking-consent__backdrop--entry">
          <section
            className={`tracking-consent${isPurchaseRoute ? ' tracking-consent--event-route' : ''}`}
            role="dialog"
            aria-modal="false"
            aria-label="Privacy choices"
            aria-describedby="tracking-consent-entry-copy"
          >
            <h2>{variant === 'benefit_clarity' ? 'Help improve your event journey' : 'Your privacy choices'}</h2>
            <p id="tracking-consent-entry-copy">
              {variant === 'benefit_clarity'
                ? 'Optional analytics and advertising measurement help us understand which event adverts work and improve booking. They stay off unless you accept. We count this privacy journey only to improve the site; you can object in Manage choices.'
                : 'Choose whether optional analytics and advertising measurement can start. Essential-only browsing works normally. We count this privacy journey only to improve the site; you can object in Manage choices.'}{' '}
              <Link to="/privacy">Privacy information</Link>
            </p>
            <div className="tracking-consent__actions">
              <button type="button" onClick={() => save({ analytics: true, marketing: true }, 'accept_all')}>
                Accept all
              </button>
              <button type="button" onClick={() => save({ analytics: false, marketing: false }, 'reject_nonessential')}>
                Reject nonessential
              </button>
              <button type="button" onClick={openPreferences}>
                Manage choices
              </button>
            </div>
            <button type="button" className="tracking-consent__dismiss" onClick={dismiss}>
              Continue without choosing
            </button>
          </section>
        </div>
      )}

      {showPreferences && (
        <div className="tracking-consent__backdrop">
          <section
            className="tracking-consent__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tracking-consent-title"
            ref={dialogRef}
            tabIndex="-1"
          >
            <h2 id="tracking-consent-title">Privacy choices</h2>
            <p>Optional technologies stay off unless you switch them on. Provider detail is in our <Link to="/privacy">privacy policy</Link>.</p>

            <label className="tracking-consent__category">
              <div>
                <strong>Analytics</strong>
                <p>Helps measure site use and performance.</p>
              </div>
              <input
                type="checkbox"
                checked={analytics}
                onChange={(event) => setAnalytics(event.target.checked)}
              />
            </label>

            <label className="tracking-consent__category">
              <div>
                <strong>Marketing</strong>
                <p>Enables advertising measurement and conversion matching.</p>
              </div>
              <input
                type="checkbox"
                checked={marketing}
                onChange={(event) => setMarketing(event.target.checked)}
              />
            </label>

            <div className="tracking-consent__actions tracking-consent__actions--dialog">
              <button type="button" onClick={() => save({ analytics: false, marketing: false }, isPending ? 'reject_nonessential' : 'withdraw')}>
                {isPending ? 'Reject nonessential' : 'Withdraw optional consent'}
              </button>
              <button type="button" onClick={() => save({ analytics: true, marketing: true }, 'accept_all')}>
                Accept all
              </button>
              <button type="button" onClick={() => save({ analytics, marketing }, 'settings_customised')}>
                Save choices
              </button>
            </div>

            <div className="tracking-consent__measurement">
              <h3>Site-improvement measurement</h3>
              <p>
                We count short-lived, pseudonymous consent journeys only to improve this service. It is not
                used for advertising or cross-site tracking and is deleted after 35 days.
              </p>
              <button type="button" onClick={toggleMeasurement}>
                {measurementObjected ? 'Resume site-improvement measurement' : 'Object to site-improvement measurement'}
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

export default TrackingConsent;
